import { request } from '@mairistem/tools';
import type {} from '@redux-devtools/extension';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { getRegieEncaisse } from '@/routes/regie';
import type { CacheMetadataType, EncaisseApiType } from '@/types';
import { CACHE_TTL, isCacheValid } from '@/utils/cache';

type EncaisseStoreType = {
  data: EncaisseApiType | null;
  loading: boolean;
  /** Requête en cours, y compris lors d'un rafraîchissement silencieux */
  fetching: boolean;
  error: string | null;
  cache: CacheMetadataType;

  fetch: (regieId: number, force?: boolean) => Promise<void>;
  clear: () => void;
};

const useEncaisseStore = create<EncaisseStoreType>()(
  devtools(
    (set, get) => ({
      data: null,
      loading: false,
      fetching: false,
      error: null,
      cache: {
        timestamp: null,
        regieId: null,
      },

      fetch: async (regieId: number, force = false) => {
        // Guard : éviter fetch concurrent
        if (get().fetching) return;

        // Skip si cache valide et pas de force
        const { cache } = get();
        if (!force && isCacheValid(cache.timestamp, CACHE_TTL.ENCAISSE, cache.regieId, regieId)) {
          return;
        }

        // Ne montrer le loading que si on n'a pas encore de données
        // Pour un refresh invisible à l'utilisateur
        const hasData = get().data !== null;
        set({ loading: !hasData, fetching: true, error: null });

        try {
          const data: EncaisseApiType = await request.get(getRegieEncaisse(regieId));
          set({
            data,
            loading: false,
            fetching: false,
            cache: {
              timestamp: Date.now(),
              regieId,
            },
          });
        } catch (error) {
          set({
            loading: false,
            fetching: false,
            error: (error as Error).message || "Erreur lors du chargement de l'encaisse",
          });
        }
      },

      clear: () => {
        set({
          data: null,
          loading: false,
          fetching: false,
          error: null,
          cache: { timestamp: null, regieId: null },
        });
      },
    }),
    { name: 'EncaisseStore' },
  ),
);

export default useEncaisseStore;
